import React, { useState, useEffect } from 'react';
import { PieChart as RechartsPieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { useTheme } from 'next-themes';
import { Sun, Droplets, Leaf, Wind } from 'lucide-react';
import PieChartMobile from './PieChartMobile'; // Import mobile version

interface PieChartProps {
  data: Array<{
    name: string;
    value: number;
    color: string;
    icon?: React.ReactNode;
  }>;
  width?: number | string;
  height?: number;
  innerRadius?: number | string;
  outerRadius?: number | string;
  label?: boolean;
  legend?: boolean;
  tooltip?: boolean;
  compact?: boolean;
}

export function PieChart({
  data,
  width = '100%',
  height = 300,
  innerRadius = '60%',
  outerRadius = '90%',
  label = false,
  legend = true,
  tooltip = true,
  compact = false,
}: PieChartProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  
  // Mobile detection
  const [isMobile, setIsMobile] = useState<boolean | null>(null);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Filter out zero values for better visualization and add icons
  const filteredData = data
    .filter(item => item.value > 0)
    .map(item => ({
      ...item,
      // Add icon based on energy source name
      icon: (() => {
        const normalizedType = item.name.toLowerCase();
        const size = 14;
        
        if (normalizedType.includes('solar') || normalizedType.includes('sonne')) {
          return <Sun size={size} className="text-yellow-500 mr-1.5" />;
        }
        
        if (normalizedType.includes('wasser')) {
          return <Droplets size={size} className="text-blue-500 mr-1.5" />;
        }
        
        if (normalizedType.includes('biomasse')) {
          return <Leaf size={size} className="text-green-600 mr-1.5" />;
        }
        
        if (normalizedType.includes('biogas')) {
          return <Leaf size={size} className="text-green-400 mr-1.5" />;
        }
        
        if (normalizedType.includes('wind')) {
          return <Wind size={size} className="text-sky-400 mr-1.5" />;
        }
        
        return null;
      })()
    }));

  if (isMobile === null) {
    // Return a placeholder while detecting screen size
    return <div className="w-full h-full min-h-[200px] flex items-center justify-center">...</div>;
  }
  
  if (isMobile) {
    return <PieChartMobile data={filteredData} showLegend={legend} animate={true} />;
  }

  if (filteredData.length === 0) {
    return (
      <div className="flex items-center justify-center h-full min-h-[200px] text-gray-500 dark:text-gray-400">
        Keine Daten verfügbar
      </div>
    );
  }

  return (
    <div className={`w-full ${compact ? 'h-[200px]' : 'h-[300px]'}`}>
      {/* Debug wrapper to check if ResponsiveContainer is rendering */}
      <div className="w-full h-full border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800">
        {/* Pre-render debugging */}
        {console.log('[PieChart] About to render chart with:', {
          chartType: 'PieChart',
          dataLength: filteredData.length,
          dataItems: filteredData.map(d => ({ name: d.name, value: d.value })),
          totalValue: filteredData.reduce((sum, item) => sum + item.value, 0),
          containerSize: `width=${width}, height=${height}`,
          compact
        })}
        <ResponsiveContainer 
          width={width} 
          height={height}
          minHeight={compact ? 150 : 200}
          debounceMs={50}
        >
        <RechartsPieChart>
          <Pie
            data={filteredData}
            cx="50%"
            cy="50%"
            innerRadius={compact ? '50%' : innerRadius}
            outerRadius={compact ? '80%' : outerRadius}
            paddingAngle={2}
            dataKey="value"
            label={label ? renderCustomizedLabel : false}
            labelLine={false}
          >
            {filteredData.map((entry, index) => (
              <Cell 
                key={`cell-${index}`} 
                fill={entry.color} 
                stroke={isDark ? '#1f2937' : '#fff'}
                strokeWidth={1}
              />
            ))}
          </Pie>
          {tooltip && (
            <Tooltip 
              contentStyle={{
                backgroundColor: isDark ? '#1f2937' : '#fff',
                border: `1px solid ${isDark ? '#374151' : '#e5e7eb'}`,
                borderRadius: '0.5rem',
                padding: '0.5rem',
                fontSize: '0.875rem',
              }}
              itemStyle={{
                color: isDark ? '#f3f4f6' : '#111827',
              }}
              formatter={(value: number) => {
                const total = filteredData.reduce((sum, item) => sum + item.value, 0);
                const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                return `${value.toLocaleString('de-AT')} kWh (${percentage}%)`;
              }}
            />
          )}
          {legend && (
            <Legend 
              layout={compact ? 'horizontal' : 'vertical'}
              verticalAlign={compact ? 'bottom' : 'middle'}
              align={compact ? 'center' : 'right'}
              wrapperStyle={{
                paddingTop: compact ? '1rem' : 0,
                paddingLeft: compact ? 0 : '1rem',
                fontSize: '0.75rem',
              }}
              content={({ payload }) => (
                <div className={`flex ${compact ? 'flex-wrap justify-center gap-x-4 gap-y-1' : 'flex-col gap-1'}`}>
                  {payload?.map((entry, index) => {
                    const dataItem = filteredData.find(item => item.name === entry.value);
                    return (
                      <div 
                        key={`legend-${index}`} 
                        className="flex items-center text-xs text-gray-600 dark:text-gray-300"
                        style={{ color: entry.color }}
                      >
                        {dataItem?.icon || <span className="w-3.5 h-3.5 rounded-full mr-1.5" style={{ backgroundColor: entry.color }} />}
                        <span className="truncate max-w-[100px]">
                          {entry.value}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            />
          )}
        </RechartsPieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

const RADIAN = Math.PI / 180;

interface CustomLabelProps {
  cx: number;
  cy: number;
  midAngle: number;
  innerRadius: number;
  outerRadius: number;
  percent: number;
  name: string;
}

const renderCustomizedLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
  name,
}: CustomLabelProps) => {
  const radius = 25 + innerRadius + (outerRadius - innerRadius);
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="currentColor"
      textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline="central"
      className="text-[10px] fill-current text-gray-600 dark:text-gray-300"
    >
      {`${name} ${(percent * 100).toFixed(0)}%`}
    </text>
  );
};
